import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import PaymentSection, {
  PAYMENT_METHOD_LABELS,
} from "../components/PaymentSection";
import OtherAmountModal from "../components/OtherAmountModal";
import OldPurchaseTable, {
  createDefaultOldPurchaseRow,
} from "../components/OldPurchaseTable";
import "../assets/css/theme-table-header.css";
import {
  useLazyOtherChargeOptions,
  applyOtherChargeToEntry,
  queryHasTwoLetters,
} from "../utils/otherChargeOptions";
import {
  calculateOtherAmountAmount as calculateAmount,
  calculateOtherAmountLessWeight as calculateLessWeight,
  sumOtherAmountEntries,
  mapApiChargesToOtherAmountEntries,
  mapOtherAmountEntriesForCreate,
  mapOtherAmountEntriesForUpdate,
} from "../utils/otherAmountCalculations";
import {
  listBankAccounts,
  listCustomerUsers,
  showCustomerUser,
  showWhsale,
  createWhsale,
  updateWhsale,
  settlementWhsale,
  getActiveBranchId,
  isErpApiSuccess,
} from "../erpClient";
import {
  searchProductGroupSelectOptions,
  mergeProductGroupOptions,
  enrichProductGroupSelectOption,
  productGroupOptionDisplayName,
} from "../utils/productGroupOptions";
import { buildStandardPaymentLines } from "../utils/paymentLinesErpAdapter";
import {
  calcBakiGm,
  calcGrandTotal,
  bakiStatusLabel,
  createWhsalesIdempotencyKey,
  getWhsaleDetailsFromShowResponse,
  isWhsalesApiSuccess,
  mapWhsalePaymentsForApi,
  rateFromMetalGroupCarat,
  sumOutGm,
  ERP_WHSALES_LIST_INVALIDATE_EVENT,
} from "../utils/whsalesErpAdapter";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import { formatIndianRupee } from "../utils/formatLiveRate";

const RATE_ON_OPTIONS = ["FWt", "NWt", "GWt"];

const rateOnToApi = (v) => {
  const s = String(v || "FWt").trim().toUpperCase();
  if (s === "GWT" || s === "GWT.") return "GWT";
  if (s === "NWT" || s === "NWT.") return "NWT";
  return "FINE";
};

const rateOnFromApi = (v) => {
  const s = String(v || "FINE").trim().toUpperCase();
  if (s === "GWT" || s === "GWT.") return "GWt";
  if (s === "NWT" || s === "NWT.") return "NWt";
  return "FWt";
};

const emptyOutLine = () => ({
  key: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  tag_no: "",
  product_group_id: null,
  product_group_label: "",
  metal_group_id: null,
  pcs: "",
  gwt: "",
  lswt: "",
  nwt: "",
  purity: "",
  wastage: "",
  fine_wt: "",
  item_rate_on: "FWt",
  item_rate: "",
  amount: "",
  other_amt: "",
  total_amt: "",
  otherAmountEntries: [],
});

const OUT_COLUMNS = [
  { id: "no", label: "No.", width: "36px" },
  { id: "tag_no", label: "Tag No", width: "90px" },
  { id: "product_group", label: "Product Group", width: "160px" },
  { id: "pcs", label: "Pcs", width: "50px", align: "end" },
  { id: "gwt", label: "Gr.Wt", width: "70px", align: "end" },
  { id: "lswt", label: "Ls.Wt", width: "70px", align: "end" },
  { id: "nwt", label: "Net.Wt", width: "70px", align: "end" },
  { id: "purity", label: "Purity", width: "60px", align: "end" },
  { id: "wastage", label: "Wastage", width: "70px", align: "end" },
  { id: "fine_wt", label: "Fine.Wt", width: "70px", align: "end", readOnly: true },
  { id: "item_rate_on", label: "RateOn", width: "70px" },
  { id: "item_rate", label: "Rate", width: "80px", align: "end" },
  { id: "amount", label: "Amount", width: "80px", align: "end" },
  { id: "other_amt", label: "Other", width: "80px", align: "end" },
  { id: "total_amt", label: "TotalAmt", width: "90px", align: "end" },
];

function calcNetWt(gwt, lswt) {
  return Math.max(0, toApiWeight(gwt) - toApiWeight(lswt));
}

function calcFineWt(nwt, purity, wastage) {
  const n = toApiWeight(nwt);
  const p = toApiWeight(purity);
  const w = toApiWeight(wastage);
  if (!n) return 0;
  if (!p && !w) return n;
  return n * ((p + w) / 100);
}

function rateOnWeight(row) {
  const on = String(row.item_rate_on || "FWt");
  if (on === "GWt") return toApiWeight(row.gwt);
  if (on === "NWt") return toApiWeight(row.nwt);
  return toApiWeight(row.fine_wt);
}

function recalcOutDerived(row, { fromField } = {}) {
  let next = { ...row };

  const weightFields = ["gwt", "lswt", "nwt", "purity", "wastage"];
  if (!fromField || weightFields.includes(fromField)) {
    if (fromField !== "nwt") {
      const nwt = calcNetWt(next.gwt, next.lswt);
      if (next.gwt !== "" || next.lswt !== "") {
        next.nwt = nwt.toFixed(2);
      }
    }
    const fine = calcFineWt(next.nwt, next.purity, next.wastage);
    if (next.nwt !== "" || next.purity !== "" || next.wastage !== "") {
      next.fine_wt = fine.toFixed(2);
    }
  }

  const moneyFields = [
    "gwt",
    "lswt",
    "nwt",
    "purity",
    "wastage",
    "fine_wt",
    "item_rate_on",
    "item_rate",
    "amount",
    "other_amt",
  ];
  if (!fromField || moneyFields.includes(fromField)) {
    const wt = rateOnWeight(next);
    const rate = toApiWeight(next.item_rate);
    if (
      fromField !== "amount" &&
      fromField !== "other_amt" &&
      fromField !== "total_amt" &&
      rate > 0 &&
      wt > 0
    ) {
      next.amount = (wt * rate).toFixed(2);
    }
    if (fromField !== "total_amt") {
      const total =
        toApiWeight(next.amount) + toApiWeight(next.other_amt);
      next.total_amt = total ? total.toFixed(2) : "";
    }
  }
  return next;
}

function toApiWeight(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

const emptyPaymentRow = (id = 1) => ({
  id,
  pay_mode: "cash",
  type: "",
  amount: "",
  remarks: "",
});

const TH_STYLE = {
  fontSize: "0.85rem",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const CELL_INPUT_STYLE = {
  fontSize: "0.85rem",
  height: "38px",
  border: "none",
  borderRadius: 0,
  boxShadow: "none",
  background: "transparent",
};

const HEADER_LABEL_STYLE = {
  width: "55px",
  fontSize: "0.85rem",
  fontWeight: 600,
  marginBottom: 0,
  flexShrink: 0,
};

const SUMMARY_LABEL_STYLE = {
  width: "92px",
  fontSize: "0.85rem",
  marginBottom: 0,
  flexShrink: 0,
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const SUMMARY_INPUT_STYLE = {
  fontSize: "0.85rem",
  maxWidth: "130px",
};

const READONLY_SUMMARY_INPUT_STYLE = {
  ...SUMMARY_INPUT_STYLE,
  backgroundColor: "#e8f4fc",
};

function inferOldMetalType(row, billMetalType = "GOLD") {
  const name = String(row?.product_group_name || "").toUpperCase();
  if (name.includes("SILVER")) return "SILVER";
  if (name.includes("GOLD")) return "GOLD";
  return String(billMetalType || "GOLD").toUpperCase();
}

/**
 * SS-style party balance line.
 * With value → `75000.00 : Dr Amt` / `5.000 : Dr Gold`
 * Empty → `: Amt` / `: Gold` / `: Silver`
 */
function PartyBalanceLine({ value, label, decimals = 2 }) {
  const n = Number(value);
  const has = Number.isFinite(n) && n !== 0;
  const abs = has ? Math.abs(n) : 0;
  const side = !has ? "" : n > 0 ? "Dr" : "Cr";
  const color = !has ? "#212529" : n > 0 ? "#dc3545" : "#198754";
  const name = side ? `${side} ${label}` : label;
  return (
    <div
      style={{
        color,
        fontSize: "1rem",
        lineHeight: 1.5,
        whiteSpace: "nowrap",
        fontWeight: 500,
      }}
    >
      {has ? `${abs.toFixed(decimals)} : ${name}` : `: ${name}`}
    </div>
  );
}

function Wholesale({ masterGoldRate, masterSilverRate, showLiveRate = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { buttonClass, cardClass, buttonTableStyle, tableClass } =
    useButtonTableStyle();

  const tableWrapClass = `table-responsive border ${
    buttonTableStyle === "square" ? "rounded-0" : "rounded-2"
  } ${cardClass}`;
  const lineTableClass = `table table-bordered table-hover table-sm mb-0 theme-header-table ${tableClass}`;

  const editId =
    location.state?.whsaleId ??
    location.state?.id ??
    location.state?.whsale_id ??
    null;
  const isEdit = Boolean(editId);
  /** Full settlement page (from Wh Invoice Settlement button). */
  const settlementOnly = Boolean(location.state?.settlementOnly);

  const canSave = isEdit
    ? hasPermission(PERMISSIONS.WHSALE_EDIT)
    : hasPermission(PERMISSIONS.WHSALE_ADD);

  const [docNumber, setDocNumber] = useState("");
  const [billDate, setBillDate] = useState(
    () => new Date().toISOString().slice(0, 10),
  );
  const [metalType, setMetalType] = useState("GOLD");
  const [notes, setNotes] = useState("");
  // Filled from metal-group carat when a product group is selected (not 24C on load).
  const [itemRate, setItemRate] = useState("");
  const [paidGm, setPaidGm] = useState(""); // Ac Fine Cr
  /** Edit: remaining unsettled fine from API (`baki_gm` / `gm_left`). */
  const [remainingBakiGm, setRemainingBakiGm] = useState(null);
  /** Edit: remaining money from API (`remaining_amount` / `money_left`). */
  const [remainingAmount, setRemainingAmount] = useState(null);
  const [acFineDr, setAcFineDr] = useState("");
  const [acAmtCr, setAcAmtCr] = useState("");
  const [outLines, setOutLines] = useState([emptyOutLine()]);
  const [otherAmountModal, setOtherAmountModal] = useState({
    isOpen: false,
    rowIndex: null,
    entries: [],
    currentEntry: {
      chargeType: "",
      pcs: "",
      ct: "",
      wt: "",
      conPercentage: "",
      rateOn: "",
      rate: "",
      amount: "",
    },
    editingIndex: undefined,
  });
  const [otherAmountColumnWidths, setOtherAmountColumnWidths] = useState({
    no: "10px",
    chargetype: "150px",
    pcs: "20px",
    ct: "50px",
    wt: "60px",
    con: "10px",
    rateon: "50px",
    rate: "68px",
    amount: "80px",
  });
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [lastEnterPress, setLastEnterPress] = useState({
    time: 0,
    rowIndex: null,
  });
  const [chargeTypes, ensureOtherCharges] = useLazyOtherChargeOptions();
  const otherRateOnOptions = ["FIXED", "PCS", "CT", "WT"];
  const [oldLines, setOldLines] = useState(() => [
    createDefaultOldPurchaseRow(1),
  ]);
  const [oldPgOptionsCache, setOldPgOptionsCache] = useState([]);
  const [paymentRows, setPaymentRows] = useState([emptyPaymentRow(1)]);
  const [payableRows, setPayableRows] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);

  // Customer search
  const [customerId, setCustomerId] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerMobile, setCustomerMobile] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerDropOpen, setCustomerDropOpen] = useState(false);
  const [customerHighlightedIndex, setCustomerHighlightedIndex] = useState(0);
  const customerNameInputRef = useRef(null);
  const customerNameDropdownRef = useRef(null);
  const customerNameItemsRef = useRef([]);
  const [goldBalGm, setGoldBalGm] = useState(0);
  const [silverBalGm, setSilverBalGm] = useState(0);
  const [amtBal, setAmtBal] = useState(0);

  // Product group typeahead (RateCut-style portal dropdown)
  const [pgOptions, setPgOptions] = useState([]);
  const [activePgRow, setActivePgRow] = useState(null); // { section: 'out'|'old', index }
  const [pgApiCalled, setPgApiCalled] = useState(false);
  const [pgHighlighted, setPgHighlighted] = useState(0);
  const [pgPosition, setPgPosition] = useState({
    top: 0,
    left: 0,
    width: 160,
  });
  const [activeRateOnRow, setActiveRateOnRow] = useState(null); // out row index
  const [rateOnHighlighted, setRateOnHighlighted] = useState(0);
  const [rateOnPosition, setRateOnPosition] = useState({
    top: 0,
    left: 0,
    width: 70,
  });
  const idempotencyRef = useRef(null);
  const outInputRefs = useRef({});
  const addingOutRowRef = useRef(false);
  const pgItemsRef = useRef([]);
  const pgSearchSeq = useRef(0);

  const totalOutGm = useMemo(
    () =>
      sumOutGm(
        outLines.map((r) => ({
          gwt: r.gwt,
          nwt: r.nwt,
          fine_wt: r.fine_wt,
          item_rate_on: r.item_rate_on,
        })),
      ),
    [outLines],
  );

  const paidGmNum = paidGm === "" || paidGm == null ? 0 : toApiWeight(paidGm);
  const rateNum = toApiWeight(itemRate);
  /**
   * Old-metal fine that counts as gm credit.
   * Rows with a manually entered Total Amt are money-form — skip their fine wt.
   */
  const oldMetalFineGmCredit = useMemo(
    () =>
      oldLines.reduce((s, r) => {
        const amt = toApiWeight(r.total_amt);
        if (r.isTotalAmtManual && amt > 0) return s;
        return s + toApiWeight(r.fine_wt);
      }, 0),
    [oldLines],
  );
  /** Tot Fine Cr = Ac Fine Cr + old-metal Fine Wt (gm-form rows only). */
  const totFineCrGm = +(paidGmNum + oldMetalFineGmCredit).toFixed(3);
  const bakiGm = calcBakiGm(totalOutGm, oldMetalFineGmCredit, paidGmNum);
  /**
   * Os Fine: create = Tot Fine Dr − Tot Fine Cr.
   * Edit/settlement = loaded baki − (Ac Fine Cr + old-metal Fine Wt this session).
   */
  const osFineGm = Math.max(
    0,
    +(
      isEdit && remainingBakiGm != null
        ? remainingBakiGm - paidGmNum - oldMetalFineGmCredit
        : totalOutGm - totFineCrGm
    ).toFixed(3),
  );
  const grandTotal = calcGrandTotal(paidGmNum, rateNum);
  const statusLabel = bakiStatusLabel(
    isEdit && remainingBakiGm != null ? osFineGm : bakiGm,
    metalType,
  );

  const totalPaidMoney = useMemo(
    () =>
      paymentRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0) +
      payableRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0),
    [paymentRows, payableRows],
  );

  useEffect(() => {
    (async () => {
      try {
        const res = await listBankAccounts({});
        const banks = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.bank_accounts)
            ? res.data.bank_accounts
            : Array.isArray(res?.data?.items)
              ? res.data.items
              : [];
        setBankAccounts(banks);
        setPaymentTypes(
          banks.map((b) => ({
            id: b.id,
            value: String(b.id),
            label: b.bankName || b.bank_name || b.accountNumber || `Bank ${b.id}`,
            accountType: b.accountType || b.account_type,
          })),
        );
      } catch (e) {
        console.warn("bank load failed", e);
      }
    })();
  }, []);

  // Load edit
  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    (async () => {
      setLoadingEdit(true);
      try {
        const res = await showWhsale({ id: editId });
        const details = getWhsaleDetailsFromShowResponse(res);
        if (!details || cancelled) {
          if (!details) toast.error("Could not load wholesale bill");
          return;
        }
        setDocNumber(details.doc_number || "");
        setBillDate(
          details.date
            ? String(details.date).slice(0, 10)
            : new Date().toISOString().slice(0, 10),
        );
        setMetalType(details.metal_type || "GOLD");
        setItemRate(
          String(
            details.item_rate ||
              details.rate_per_gm ||
              details.ratePerGm ||
              "",
          ),
        );
        // Ac Fine Cr was already settled on create — leave blank for new gm on edit.
        setPaidGm("");
        setRemainingBakiGm(
          details.baki_gm != null
            ? Number(details.baki_gm)
            : details.gm_left != null
              ? Number(details.gm_left)
              : null,
        );
        setRemainingAmount(
          details.remaining_amount != null
            ? Number(details.remaining_amount)
            : details.money_left != null
              ? Number(details.money_left)
              : null,
        );
        setNotes(details.notes || "");
        setCustomerId(details.customer_id);
        setCustomerSearch(details.customer_name || "");
        setCustomerMobile(details.customer_mobile || "");
        setGoldBalGm(details.customer_gold_balance_gm || 0);
        setSilverBalGm(details.customer_silver_balance_gm || 0);
        setAmtBal(
          parseFloat(
            details.customer_balance ??
              details.customer_users_balance ??
              details.customer_sale_balance ??
              0,
          ) || 0,
        );

        const outs = (details.items || []).map((it) =>
          recalcOutDerived({
            key: `out-${it.id || Math.random()}`,
            id: it.id,
            tag_no: it.tag_no || it.tagNo || "",
            product_group_id: it.product_group_id ?? null,
            product_group_label:
              it.product_group_name || it.product_group || "",
            metal_group_id: it.metal_group_id ?? null,
            pcs: it.pcs != null ? String(it.pcs) : "",
            gwt: it.gwt != null ? String(it.gwt) : "",
            lswt:
              it.lwt != null
                ? String(it.lwt)
                : it.lswt != null
                  ? String(it.lswt)
                  : it.less_wt != null
                    ? String(it.less_wt)
                    : "",
            nwt: it.nwt != null ? String(it.nwt) : "",
            purity: it.purity != null ? String(it.purity) : "",
            wastage: it.wastage != null ? String(it.wastage) : "",
            fine_wt: it.fine_wt != null ? String(it.fine_wt) : "",
            item_rate_on: rateOnFromApi(it.item_rate_on),
            item_rate:
              it.item_rate != null
                ? String(it.item_rate)
                : it.rate != null
                  ? String(it.rate)
                  : "",
            amount:
              it.amount != null
                ? String(it.amount)
                : it.it_amt != null
                  ? String(it.it_amt)
                  : "",
            other_amt:
              it.other_amt != null
                ? String(it.other_amt)
                : it.oth_amt != null
                  ? String(it.oth_amt)
                  : "",
            total_amt:
              it.total_amt != null
                ? String(it.total_amt)
                : it.totalAmt != null
                  ? String(it.totalAmt)
                  : "",
            otherAmountEntries: mapApiChargesToOtherAmountEntries(
              it.charges || it.other_charges || it.otherAmountEntries || [],
            ),
          }),
        );
        setOutLines(outs.length ? outs : [emptyOutLine()]);

        const olds = (details.old_metal || []).map((it, i) => ({
          ...createDefaultOldPurchaseRow(
            Number(it.id) > 0 ? Number(it.id) : i + 1,
          ),
          serverId: it.id ?? null,
          product_group_id: it.product_group_id ?? "",
          product_group_name:
            it.product_group_name || it.product_group || "",
          metal_group_id: it.metal_group_id ?? null,
          pcs: it.pcs != null ? String(it.pcs) : "",
          ct: it.ct != null ? String(it.ct) : "",
          gwt: it.gwt != null ? String(it.gwt) : "",
          lwt:
            it.lwt != null
              ? String(it.lwt)
              : it.lswt != null
                ? String(it.lswt)
                : "",
          nwt: it.nwt != null ? String(it.nwt) : "",
          touch:
            it.touch != null
              ? String(it.touch)
              : it.purity != null
                ? String(it.purity)
                : "",
          fine_wt: it.fine_wt != null ? String(it.fine_wt) : "",
          total_amt:
            it.total_amt != null
              ? String(it.total_amt)
              : it.amount != null
                ? String(it.amount)
                : "",
          isTotalAmtManual: Boolean(it.total_amt != null || it.amount != null),
        }));
        setOldLines(olds.length ? olds : [createDefaultOldPurchaseRow(1)]);

        if (settlementOnly) {
          // New settlement payments only — leave Cash blank (Net Amt shows Dr Amt).
          setPaymentRows([emptyPaymentRow(1)]);
        } else if (details.payments?.length) {
          // Merge same pay_mode (e.g. 2× Cash → one Cash row with total amt)
          const mergedByMode = new Map();
          details.payments.forEach((p, i) => {
            const mode = String(p.pay_mode || "cash").toLowerCase();
            const amt = parseFloat(p.amount) || 0;
            const existing = mergedByMode.get(mode);
            if (!existing) {
              mergedByMode.set(mode, {
                id: p.id || i + 1,
                pay_mode: mode,
                type: p.bank_account_id ? String(p.bank_account_id) : "",
                amount: amt,
                remarks: p.remarks || "",
                paymentId: p.id,
                paymentIdempotencyKey: p.idempotency_key,
              });
            } else {
              existing.amount += amt;
              if (!existing.type && p.bank_account_id) {
                existing.type = String(p.bank_account_id);
              }
              if (p.remarks) {
                existing.remarks = existing.remarks
                  ? `${existing.remarks}; ${p.remarks}`
                  : p.remarks;
              }
            }
          });
          setPaymentRows(
            Array.from(mergedByMode.values()).map((row, i) => ({
              ...row,
              id: row.id || i + 1,
              amount: row.amount ? String(+row.amount.toFixed(2)) : "",
            })),
          );
        }
      } catch (e) {
        console.error(e);
        toast.error("Failed to load wholesale");
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editId, settlementOnly]);

  // Customer search (3+ chars)
  useEffect(() => {
    if (customerId) return;
    const q = customerSearch.trim();
    if (q.length < 2) {
      setCustomerOptions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await listCustomerUsers({ search: q });
        const list = Array.isArray(res?.data?.customers)
          ? res.data.customers
          : Array.isArray(res?.data?.items)
            ? res.data.items
            : Array.isArray(res?.data)
              ? res.data
              : [];
        setCustomerOptions(list);
        setCustomerHighlightedIndex(0);
        setCustomerDropOpen(true);
      } catch {
        setCustomerOptions([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [customerSearch, customerId]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        customerNameDropdownRef.current &&
        !customerNameDropdownRef.current.contains(event.target) &&
        customerNameInputRef.current &&
        !customerNameInputRef.current.contains(event.target)
      ) {
        setCustomerDropOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectCustomer = async (c) => {
    setCustomerId(c.id);
    const name = c.name || c.fullName || "";
    const mobile = c.mobile || "";
    setCustomerSearch(name);
    setCustomerMobile(mobile);
    setCustomerDropOpen(false);
    setGoldBalGm(
      parseFloat(c.gold_balance_gm ?? c.goldBalanceGm ?? c.gold ?? 0) || 0,
    );
    setSilverBalGm(
      parseFloat(c.silver_balance_gm ?? c.silverBalanceGm ?? c.silver ?? 0) || 0,
    );
    setAmtBal(
      parseFloat(
        c.users_balance ?? c.balance ?? c.sale_balance ?? c.saleBalance ?? 0,
      ) || 0,
    );
    try {
      const res = await showCustomerUser({ id: c.id });
      const raw =
        res?.data?.customer ??
        res?.data?.user ??
        res?.data?.item ??
        (Array.isArray(res?.data?.customers) ? res.data.customers[0] : null);
      if (raw) {
        setCustomerMobile(raw.mobile || mobile);
        setGoldBalGm(
          parseFloat(raw.gold_balance_gm ?? raw.gold ?? goldBalGm) || 0,
        );
        setSilverBalGm(
          parseFloat(raw.silver_balance_gm ?? raw.silver ?? silverBalGm) || 0,
        );
        setAmtBal(
          parseFloat(
            raw.users_balance ??
              raw.balance ??
              raw.sale_balance ??
              raw.saleBalance ??
              0,
          ) || 0,
        );
      }
    } catch {
      /* keep list values */
    }
  };

  const positionPgDropdown = useCallback((section, index) => {
    if (section !== "out") return;
    const el = outInputRefs.current[`${index}-product_group`];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPgPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: Math.max(rect.width, 200),
    });
  }, []);

  const closePgDropdown = useCallback(() => {
    setActivePgRow(null);
    setPgOptions([]);
    setPgApiCalled(false);
    setPgHighlighted(0);
  }, []);

  const searchProductGroups = async (text, section, index) => {
    const q = String(text || "").trim();
    setActivePgRow({ section, index });
    setPgHighlighted(0);
    positionPgDropdown(section, index);

    if (q.length < 3) {
      setPgOptions([]);
      setPgApiCalled(false);
      return;
    }

    const seq = ++pgSearchSeq.current;
    setPgApiCalled(false);
    try {
      const opts = await searchProductGroupSelectOptions(q, {
        metal_type: metalType,
      });
      if (seq !== pgSearchSeq.current) return;
      setPgOptions(mergeProductGroupOptions([], opts));
      setPgApiCalled(true);
      positionPgDropdown(section, index);
    } catch {
      if (seq !== pgSearchSeq.current) return;
      setPgOptions([]);
      setPgApiCalled(true);
    }
  };

  const applyProductGroup = async (section, index, opt) => {
    if (!opt) return;
    let enriched = opt;
    try {
      enriched = (await enrichProductGroupSelectOption(opt)) || opt;
    } catch {
      enriched = opt;
    }
    const label = productGroupOptionDisplayName(enriched);
    const purityRaw = enriched.purity;
    const patch = {
      product_group_id: enriched.value ?? enriched.id,
      product_group_label: label,
      metal_group_id:
        enriched.metal_group_id ?? enriched.metalGroupId ?? null,
      ...(purityRaw !== "" && purityRaw != null
        ? { purity: String(purityRaw) }
        : {}),
    };
    if (section === "out") {
      setOutLines((prev) =>
        prev.map((r, i) =>
          i === index
            ? recalcOutDerived({ ...r, ...patch }, { fromField: "purity" })
            : r,
        ),
      );
      // Metal-group carat → matching gold/silver master rate into 1gm Rate
      const nextRate = rateFromMetalGroupCarat({
        carat: enriched.carat,
        metal: enriched.metal || metalType,
        label,
        masterGoldRate,
        masterSilverRate,
        showLiveRate,
      });
      if (nextRate > 0) {
        setItemRate(String(nextRate));
        const metalFromPg = String(enriched.metal || "").toUpperCase();
        if (metalFromPg.includes("SILVER")) setMetalType("SILVER");
        else if (metalFromPg.includes("GOLD")) setMetalType("GOLD");
      }
      setTimeout(() => {
        outInputRefs.current[`${index}-pcs`]?.focus();
      }, 50);
    }
    closePgDropdown();
  };

  const onPgKeyDown = (e, section, index) => {
    const open =
      activePgRow?.section === section && activePgRow?.index === index;
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setPgHighlighted((h) => {
        const next = Math.min(h + 1, Math.max(pgOptions.length - 1, 0));
        pgItemsRef.current[next]?.scrollIntoView({ block: "nearest" });
        return next;
      });
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setPgHighlighted((h) => {
        const next = Math.max(h - 1, 0);
        pgItemsRef.current[next]?.scrollIntoView({ block: "nearest" });
        return next;
      });
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (pgOptions[pgHighlighted]) {
        applyProductGroup(section, index, pgOptions[pgHighlighted]);
      }
      return;
    }
    if (e.key === "Escape" || e.key === "Tab") {
      closePgDropdown();
    }
  };

  const updateOut = (index, field, value) => {
    setOutLines((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        return recalcOutDerived({ ...r, [field]: value }, { fromField: field });
      }),
    );
  };

  const formatAmount = (value) => {
    if (!value || isNaN(value)) return "";
    const num = parseFloat(value);
    const decimalPart = num - Math.floor(num);
    return decimalPart >= 0.5
      ? Math.ceil(num).toString()
      : Math.floor(num).toString();
  };

  const formatLessWeight = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return "0.00";
    return num.toFixed(2);
  };

  const applyOtherEntriesToOutRow = (row, finalEntries) => {
    const totalAmount = finalEntries.reduce(
      (sum, entry) => sum + (parseFloat(entry.amount) || 0),
      0,
    );
    const totalLessWeight = finalEntries.reduce((sum, entry) => {
      const lessWeight = calculateLessWeight(entry);
      return sum + (isNaN(lessWeight) ? 0 : lessWeight);
    }, 0);
    const next = {
      ...row,
      other_amt: formatAmount(totalAmount) || String(totalAmount.toFixed(2)),
      otherAmountEntries: finalEntries,
    };
    if (totalLessWeight > 0) {
      next.lswt = formatLessWeight(totalLessWeight);
    }
    return recalcOutDerived(next, { fromField: "other_amt" });
  };

  const openOtherAmountModal = (rowIndex) => {
    const row = outLines[rowIndex];
    if (!row) return;
    ensureOtherCharges();
    const currentEntries = row.otherAmountEntries || [];
    const currentValue = row.other_amt || "";
    const initialEntries =
      currentEntries.length > 0
        ? currentEntries.map((entry) => ({
            ...entry,
            rateOn: entry.rateOn || "",
          }))
        : currentValue
          ? [
              {
                chargeType: "Direct Entry",
                pcs: "",
                ct: "",
                wt: "",
                conPercentage: "",
                rateOn: "",
                rate: "",
                amount: currentValue,
              },
            ]
          : [];

    setOtherAmountModal({
      isOpen: true,
      rowIndex,
      entries: [...initialEntries],
      currentEntry: {
        chargeType: "",
        pcs: "",
        ct: "",
        wt: "",
        conPercentage: "",
        rateOn: "",
        rate: "",
        amount: "",
      },
      editingIndex: undefined,
    });
  };

  const handleSaveOtherAmountEntries = () => {
    const { rowIndex, entries, currentEntry } = otherAmountModal;
    if (rowIndex == null) return;

    let finalEntries = [...entries];
    if (currentEntry.amount && currentEntry.chargeType) {
      if (isNaN(currentEntry.amount)) {
        toast.error("Please enter a valid amount");
        return;
      }
      finalEntries.push({ ...currentEntry });
    }

    setOutLines((prev) =>
      prev.map((r, i) =>
        i === rowIndex ? applyOtherEntriesToOutRow(r, finalEntries) : r,
      ),
    );
    setOtherAmountModal((prev) => ({ ...prev, isOpen: false }));
  };

  const handleOtherAmountInteraction = (rowIndex, event) => {
    if (event.type === "dblclick") {
      event.preventDefault();
      openOtherAmountModal(rowIndex);
    } else if (event.type === "keydown" && event.key === "Enter") {
      const now = Date.now();
      if (
        lastEnterPress.rowIndex === rowIndex &&
        now - lastEnterPress.time < 500
      ) {
        event.preventDefault();
        openOtherAmountModal(rowIndex);
        setLastEnterPress({ time: 0, rowIndex: null });
      } else {
        setLastEnterPress({ time: now, rowIndex });
      }
    }
  };

  const getSuggestions = (query) => {
    if (queryHasTwoLetters(query)) ensureOtherCharges();
    if (!query || query.length < 1) {
      setSuggestions([]);
      return;
    }
    const searchQuery = query.toLowerCase();
    let matchedByCode = chargeTypes.filter((charge) =>
      charge.codes.some((code) => code.toLowerCase() === searchQuery),
    );
    if (matchedByCode.length === 0) {
      matchedByCode = chargeTypes.filter((charge) =>
        charge.codes.some((code) => code.toLowerCase().startsWith(searchQuery)),
      );
    }
    const matchedByName = chargeTypes.filter(
      (charge) =>
        charge.name.toLowerCase().includes(searchQuery) &&
        !matchedByCode.some((codeMatch) => codeMatch.name === charge.name),
    );
    setSuggestions(
      [...matchedByCode, ...matchedByName].map(
        (charge) => `${charge.name} (${charge.codes[0] || ""})`,
      ),
    );
  };

  const handleOtherAmountColumnWidthChange = (column, newWidth) => {
    setOtherAmountColumnWidths((prev) => ({
      ...prev,
      [column]: `${newWidth}px`,
    }));
  };

  const handleRateOnDropdown = (rowIndex, value, isCurrentEntry = false) => {
    if (isCurrentEntry) {
      setOtherAmountModal((prev) => {
        const updatedEntry = { ...prev.currentEntry, rateOn: value };
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        return { ...prev, currentEntry: updatedEntry };
      });
    } else {
      setOtherAmountModal((prev) => {
        const newEntries = [...prev.entries];
        const updatedEntry = { ...newEntries[rowIndex], rateOn: value };
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        newEntries[rowIndex] = updatedEntry;
        return { ...prev, entries: newEntries };
      });
    }
  };

  const handleRateOnKeyDown = (e, rowIndex, isCurrentEntry = false) => {
    const currentValue = isCurrentEntry
      ? otherAmountModal.currentEntry.rateOn
      : otherAmountModal.entries[rowIndex]?.rateOn;
    const filtered = otherRateOnOptions.filter((option) =>
      option.toLowerCase().includes((currentValue || "").toLowerCase()),
    );
    if (filtered.length === 0) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filtered.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filtered[highlightedIndex]) {
          handleRateOnDropdown(
            rowIndex,
            filtered[highlightedIndex],
            isCurrentEntry,
          );
        }
        break;
      case "Escape":
        e.preventDefault();
        break;
      default:
        break;
    }
  };

  /** Shim so OtherAmountModal Enter-save works with flat outLines. */
  const setTableDataFromOtherModal = (updater) => {
    setOutLines((prev) => {
      const fake = prev.map((r) => ({
        data: [
          {
            id: "othAmt",
            value: r.other_amt,
            otherAmountEntries: r.otherAmountEntries || [],
          },
          { id: "lsWt", value: r.lswt },
        ],
      }));
      const next = typeof updater === "function" ? updater(fake) : fake;
      if (!Array.isArray(next)) return prev;
      return prev.map((r, i) => {
        const row = next[i];
        if (!row?.data) return r;
        const oth = row.data.find((c) => c.id === "othAmt");
        if (!oth) return r;
        return applyOtherEntriesToOutRow(
          r,
          oth.otherAmountEntries || [],
        );
      });
    });
  };

  const handleAddOutRow = useCallback((focusTag = true) => {
    if (addingOutRowRef.current) return;
    addingOutRowRef.current = true;
    setOutLines((prev) => {
      const newIndex = prev.length;
      const next = [...prev, emptyOutLine()];
      setTimeout(() => {
        if (focusTag) {
          const el = outInputRefs.current[`${newIndex}-tag_no`];
          if (el) el.focus();
        }
        addingOutRowRef.current = false;
      }, 80);
      return next;
    });
  }, []);

  const onOutTotalAmtFocus = (rowIndex) => {
    if (rowIndex === outLines.length - 1) {
      handleAddOutRow(true);
    }
  };

  const onOutKeyDown = (e, rowIndex, colId) => {
    if (e.key === "Tab" && !e.shiftKey && colId === "total_amt") {
      if (rowIndex === outLines.length - 1) {
        e.preventDefault();
        handleAddOutRow(true);
      }
    }
  };

  const sumOutField = (field) =>
    outLines.reduce((s, r) => s + toApiWeight(r[field]), 0);

  const avgOutField = (field) =>
    outLines.length ? sumOutField(field) / outLines.length : 0;

  // When paid_gm blank, treat remaining after old-in as unpaid (baki = out - old)
  // Primary input is paid_gm; default suggestion = totalOut - oldIn (full pay in money? No - user example paid partial)
  // Default: leave paid empty → fully unpaid money-wise but baki = out - old (fully unpaid gm)
  // User said: if not touch paid → fully paid like sale. For NEW model:
  // "Paid gm (editable) — gm converted to money"
  // Default empty paid_gm → treat as 0 paid? Or full?
  // New formula: baki = out - old - paid. If paid untouched, prefer paid = out - old (zero baki) OR paid = 0.
  // Spec example leaves paid as user entry. I'll default paidGm empty → paid=0 so baki shows full out-old.

  const fillPaidToClearBaki = () => {
    const clear =
      isEdit && remainingBakiGm != null
        ? Math.max(
            0,
            +(Number(remainingBakiGm) - oldMetalFineGmCredit).toFixed(3),
          )
        : Math.max(0, +(totalOutGm - oldMetalFineGmCredit).toFixed(3));
    setPaidGm(String(clear));
  };

  const syncCashPaymentToGrand = useCallback(() => {
    const target =
      settlementOnly && paidGmNum <= 0
        ? Math.max(0, Number(remainingAmount) || 0)
        : grandTotal;
    setPaymentRows((prev) => {
      if (!prev.length) {
        return [{ ...emptyPaymentRow(1), amount: String(target || "") }];
      }
      const cashIdx = prev.findIndex(
        (r) => String(r.pay_mode).toLowerCase() === "cash",
      );
      if (cashIdx >= 0) {
        const next = [...prev];
        const others = next.reduce(
          (s, r, i) =>
            i === cashIdx ? s : s + (parseFloat(r.amount) || 0),
          0,
        );
        const cashAmt = Math.max(0, +(target - others).toFixed(2));
        next[cashIdx] = {
          ...next[cashIdx],
          amount: cashAmt ? String(cashAmt) : "",
        };
        return next;
      }
      return prev;
    });
  }, [grandTotal, settlementOnly, paidGmNum, remainingAmount]);

  const buildPayload = () => {
    const items = outLines
      .filter((r) => r.product_group_id || toApiWeight(r.nwt) || toApiWeight(r.gwt))
      .map((r) => ({
        ...(r.id ? { id: r.id } : {}),
        ...(r.tag_no ? { tag_no: String(r.tag_no).trim() } : {}),
        metal_group_id: r.metal_group_id ? Number(r.metal_group_id) : null,
        product_group_id: r.product_group_id
          ? Number(r.product_group_id)
          : null,
        pcs: Number(r.pcs) || 0,
        gwt: toApiWeight(r.gwt),
        ...(r.lswt !== "" && r.lswt != null
          ? { lwt: toApiWeight(r.lswt) }
          : {}),
        nwt: toApiWeight(r.nwt || r.gwt),
        ...(r.purity !== "" && r.purity != null
          ? { purity: toApiWeight(r.purity) }
          : {}),
        ...(r.wastage !== "" && r.wastage != null
          ? { wastage: toApiWeight(r.wastage) }
          : {}),
        ...(r.fine_wt !== "" && r.fine_wt != null
          ? { fine_wt: toApiWeight(r.fine_wt) }
          : {}),
        item_rate_on: rateOnToApi(r.item_rate_on),
        ...(r.item_rate !== "" && r.item_rate != null
          ? { item_rate: toApiWeight(r.item_rate) }
          : {}),
        ...(r.amount !== "" && r.amount != null
          ? { amount: toApiWeight(r.amount) }
          : {}),
        ...(r.other_amt !== "" && r.other_amt != null
          ? { other_amt: toApiWeight(r.other_amt) }
          : {}),
        ...(r.total_amt !== "" && r.total_amt != null
          ? { total_amt: toApiWeight(r.total_amt) }
          : {}),
        ...(Array.isArray(r.otherAmountEntries) && r.otherAmountEntries.length
          ? {
              charges: isEdit
                ? mapOtherAmountEntriesForUpdate(r.otherAmountEntries)
                : mapOtherAmountEntriesForCreate(r.otherAmountEntries),
            }
          : {}),
      }));

    const old_metal = oldLines
      .filter(
        (r) =>
          r.product_group_id ||
          toApiWeight(r.nwt) ||
          toApiWeight(r.gwt) ||
          toApiWeight(r.fine_wt),
      )
      .map((r) => ({
        ...(r.serverId ? { id: Number(r.serverId) } : {}),
        metal_type: inferOldMetalType(r, metalType),
        metal_group_id: r.metal_group_id ? Number(r.metal_group_id) : null,
        product_group_id: r.product_group_id
          ? Number(r.product_group_id)
          : null,
        pcs: Number(r.pcs) || 0,
        ...(r.ct !== "" && r.ct != null ? { ct: toApiWeight(r.ct) } : {}),
        gwt: toApiWeight(r.gwt),
        ...(r.lwt !== "" && r.lwt != null ? { lwt: toApiWeight(r.lwt) } : {}),
        nwt: toApiWeight(r.nwt || r.gwt),
        ...(r.touch !== "" && r.touch != null
          ? { touch: toApiWeight(r.touch) }
          : {}),
        fine_wt: toApiWeight(r.fine_wt || r.nwt || r.gwt),
        ...(r.total_amt !== "" && r.total_amt != null
          ? { total_amt: toApiWeight(r.total_amt) }
          : {}),
        item_rate_on: "FINE",
      }));

    const stdPayments = buildStandardPaymentLines(paymentRows, {
      isUpdate: isEdit,
      paymentTypes,
      payModeLabels: PAYMENT_METHOD_LABELS,
      createKey: createWhsalesIdempotencyKey,
      excludeAdvance: true,
    });
    const payments = mapWhsalePaymentsForApi(stdPayments);

    const branch_id = getActiveBranchId();

    return {
      ...(isEdit ? { id: Number(editId) } : {}),
      ...(branch_id ? { branch_id: Number(branch_id) } : {}),
      customer_id: Number(customerId),
      date: billDate,
      metal_type: metalType,
      item_rate: rateNum,
      // Edit: only send paid_gm when user enters a new Ac Fine Cr (don't wipe settled).
      ...(isEdit
        ? paidGm !== "" && paidGm != null
          ? { paid_gm: paidGmNum }
          : {}
        : { paid_gm: paidGmNum }),
      baki_gm: bakiGm,
      grand_total: grandTotal,
      notes: notes || null,
      items,
      old_metal,
      payments,
    };
  };

  const buildSettlementOldMetal = () =>
    oldLines
      .filter(
        (r) =>
          r.product_group_id ||
          toApiWeight(r.nwt) ||
          toApiWeight(r.gwt) ||
          toApiWeight(r.fine_wt),
      )
      .map((r) => ({
        ...(r.serverId ? { id: Number(r.serverId) } : {}),
        metal_type: inferOldMetalType(r, metalType),
        metal_group_id: r.metal_group_id ? Number(r.metal_group_id) : null,
        product_group_id: r.product_group_id
          ? Number(r.product_group_id)
          : null,
        pcs: Number(r.pcs) || 0,
        ...(r.ct !== "" && r.ct != null ? { ct: toApiWeight(r.ct) } : {}),
        gwt: toApiWeight(r.gwt),
        ...(r.lwt !== "" && r.lwt != null ? { lwt: toApiWeight(r.lwt) } : {}),
        nwt: toApiWeight(r.nwt || r.gwt),
        ...(r.touch !== "" && r.touch != null
          ? { touch: toApiWeight(r.touch) }
          : {}),
        fine_wt: toApiWeight(r.fine_wt || r.nwt || r.gwt),
        ...(r.total_amt !== "" && r.total_amt != null
          ? { total_amt: toApiWeight(r.total_amt) }
          : {}),
        item_rate_on: "FINE",
      }));

  const handleSettlementSave = async () => {
    if (!editId) return;
    if (!hasPermission(PERMISSIONS.WHSALE_EDIT)) {
      toast.error("No permission");
      return;
    }
    if (rateNum <= 0 && paidGmNum > 0) {
      toast.error("Enter 1gm rate");
      return;
    }
    // Overpay allowed on settlement — extra stays as customer jama (advance).
    const rem = remainingAmount != null ? Number(remainingAmount) : 0;
    const jamaAmt = Math.max(0, +(totalPaidMoney - rem).toFixed(2));
    const stdPayments = buildStandardPaymentLines(paymentRows, {
      isUpdate: true,
      paymentTypes,
      payModeLabels: PAYMENT_METHOD_LABELS,
      createKey: createWhsalesIdempotencyKey,
      excludeAdvance: true,
    });
    const payments = mapWhsalePaymentsForApi(stdPayments);
    const old_metal = buildSettlementOldMetal();
    if (!payments.length && paidGmNum <= 0 && !old_metal.length) {
      toast.error("Enter old metal, Ac Fine Cr, or payment amount");
      return;
    }

    const acAmtDr = calcGrandTotal(paidGmNum, rateNum);
    const acAmtCrNum =
      acAmtCr === "" || acAmtCr == null ? 0 : toApiWeight(acAmtCr);
    const acFineDrNum =
      acFineDr === "" || acFineDr == null ? 0 : toApiWeight(acFineDr);
    const branch_id = getActiveBranchId();

    const payload = {
      whsale_id: Number(editId),
      ...(branch_id ? { branch_id: Number(branch_id) } : {}),
      date: billDate,
      metal_type: metalType,
      item_rate: rateNum,
      paid_gm: paidGmNum,
      tot_fine_cr: totFineCrGm,
      tot_fine_dr: 0,
      os_fine: osFineGm,
      ac_amt_dr: acAmtDr,
      ...(acAmtCrNum > 0 ? { ac_amt_cr: acAmtCrNum } : {}),
      ...(acFineDrNum > 0 ? { ac_fine_dr: acFineDrNum } : {}),
      ...(jamaAmt > 0.05 ? { jama_amount: jamaAmt } : {}),
      ...(old_metal.length ? { old_metal } : {}),
      ...(payments.length ? { payments } : {}),
      idempotency_key: createWhsalesIdempotencyKey(),
    };

    setSubmitting(true);
    try {
      const res = await settlementWhsale(payload);
      if (isWhsalesApiSuccess(res) || isErpApiSuccess(res)) {
        toast.success("Settlement saved");
        window.dispatchEvent(new CustomEvent(ERP_WHSALES_LIST_INVALIDATE_EVENT));
        navigate("/whinvoice");
      } else {
        toast.error(res?.message || "Settlement failed");
      }
    } catch (err) {
      toast.error(err?.message || "Settlement failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (settlementOnly) {
      await handleSettlementSave();
      return;
    }
    if (!canSave) {
      toast.error("No permission to save wholesale");
      return;
    }
    if (!customerId) {
      toast.error("Select a customer");
      return;
    }
    const itemsOk = outLines.some(
      (r) => r.product_group_id && (toApiWeight(r.nwt) > 0 || toApiWeight(r.gwt) > 0),
    );
    if (!itemsOk) {
      toast.error("Add at least one metal OUT line with product group and weight");
      return;
    }
    if (rateNum <= 0 && paidGmNum > 0) {
      toast.error("Enter 1gm rate");
      return;
    }
    // Partial pay / udhar allowed — unpaid stays as OS Amt. Block only overpay.
    if (grandTotal > 0 && totalPaidMoney - grandTotal > 0.05) {
      toast.error(
        `Payments (₹${totalPaidMoney.toFixed(2)}) exceed payable ₹${grandTotal.toFixed(2)}`,
      );
      return;
    }

    if (!idempotencyRef.current) {
      idempotencyRef.current = createWhsalesIdempotencyKey();
    }

    setSubmitting(true);
    try {
      const payload = {
        ...buildPayload(),
        idempotency_key: idempotencyRef.current,
      };
      const res = isEdit
        ? await updateWhsale(payload)
        : await createWhsale(payload);

      if (isWhsalesApiSuccess(res) || isErpApiSuccess(res)) {
        idempotencyRef.current = null;
        toast.success(isEdit ? "Wholesale updated" : "Wholesale saved");
        window.dispatchEvent(new CustomEvent(ERP_WHSALES_LIST_INVALIDATE_EVENT));
        const created =
          res?.data?.doc_number ||
          res?.data?.whsale?.doc_number ||
          res?.data?.id;
        if (created && !isEdit) setDocNumber(String(created));
        navigate("/whinvoice");
      } else {
        toast.error(res?.message || "Save failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Save failed",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingEdit) {
    return (
      <div className="body-wrapper">
        <div className="container-fluid p-4">Loading wholesale...</div>
      </div>
    );
  }

  const handleCustomerNameKeyDown = (e) => {
    if (!customerDropOpen || customerOptions.length === 0) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setCustomerHighlightedIndex((prev) => {
          const next =
            prev < customerOptions.length - 1 ? prev + 1 : prev;
          customerNameItemsRef.current[next]?.scrollIntoView({
            block: "nearest",
            behavior: "smooth",
          });
          return next;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setCustomerHighlightedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : 0;
          customerNameItemsRef.current[next]?.scrollIntoView({
            block: "nearest",
            behavior: "smooth",
          });
          return next;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (customerOptions[customerHighlightedIndex]) {
          selectCustomer(customerOptions[customerHighlightedIndex]);
        }
        break;
      case "Escape":
      case "Tab":
        setCustomerDropOpen(false);
        break;
      default:
        break;
    }
  };

  const renderCustomerNameDropdown = () => {
    if (!customerDropOpen || customerId) return null;

    const inputRect = customerNameInputRef.current?.getBoundingClientRect();
    if (!inputRect) return null;

    const inputWidth = customerNameInputRef.current?.offsetWidth || 150;
    customerNameItemsRef.current = [];
    const portal =
      document.getElementById("dropdown-portal") || document.body;

    return ReactDOM.createPortal(
      <div
        ref={customerNameDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${inputRect.bottom + window.scrollY}px`,
          left: `${inputRect.left + window.scrollX}px`,
          width: `${inputWidth}px`,
          minWidth: `${inputWidth}px`,
          maxWidth: `${Math.max(inputWidth, 220)}px`,
          maxHeight: "150px",
          overflowY: "auto",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
          boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
          fontSize: "12px",
          lineHeight: "1.2",
        }}
      >
        {customerOptions.length > 0 ? (
          customerOptions.map((c, idx) => {
            const label =
              (c.name || c.fullName || "—") +
              (c.mobile ? ` - ${c.mobile}` : "");
            return (
              <div
                key={c.id ?? idx}
                ref={(el) => {
                  customerNameItemsRef.current[idx] = el;
                }}
                className={`dropdown-item ${
                  idx === customerHighlightedIndex ? "active" : ""
                }`}
                style={{
                  cursor: "pointer",
                  padding: "0.25rem 1rem",
                  fontSize: "0.85rem",
                  backgroundColor:
                    idx === customerHighlightedIndex
                      ? "var(--theme-primary)"
                      : "transparent",
                  color:
                    idx === customerHighlightedIndex ? "#fff" : "inherit",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                onMouseEnter={() => setCustomerHighlightedIndex(idx)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectCustomer(c);
                }}
                title={label}
              >
                {label}
              </div>
            );
          })
        ) : (
          <div className="dropdown-item disabled">No matching users</div>
        )}
      </div>,
      portal,
    );
  };

  const renderPgDropdown = () => {
    if (!activePgRow || activePgRow.section !== "out") return null;
    const { section, index } = activePgRow;
    const rowLabel = outLines[index]?.product_group_label;
    const q = String(rowLabel || "").trim();
    if (q.length < 3) return null;
    if (!pgApiCalled && pgOptions.length === 0) return null;

    const VISIBLE = 6;
    const ITEM_H = 35;
    const visibleItems = Math.min(
      Math.max(pgOptions.length, pgApiCalled ? 1 : 0),
      VISIBLE,
    );
    const dropdownHeight = Math.max(visibleItems, 1) * ITEM_H;
    const portal =
      document.getElementById("dropdown-portal") || document.body;

    pgItemsRef.current = [];

    return ReactDOM.createPortal(
      <div
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${pgPosition.top}px`,
          left: `${pgPosition.left}px`,
          width: `${pgPosition.width}px`,
          maxHeight: `${dropdownHeight}px`,
          height: `${dropdownHeight}px`,
          overflow: "hidden",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          backgroundColor: "#fff",
          boxSizing: "border-box",
          boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
          padding: 0,
        }}
      >
        <div
          style={{
            maxHeight: `${dropdownHeight}px`,
            height: `${dropdownHeight}px`,
            overflowY: pgOptions.length > VISIBLE ? "auto" : "hidden",
            overflowX: "hidden",
          }}
        >
          {pgOptions.length > 0 ? (
            pgOptions.map((opt, idx) => {
              const label = productGroupOptionDisplayName(opt);
              const isSelected =
                String(opt.label || opt.name || "").toLowerCase() ===
                String(rowLabel || "").toLowerCase();
              return (
                <div
                  key={opt.value ?? opt.id ?? idx}
                  ref={(el) => {
                    pgItemsRef.current[idx] = el;
                  }}
                  className={`dropdown-item ${
                    idx === pgHighlighted ? "active" : ""
                  }`}
                  title={label}
                  style={{
                    cursor: "pointer",
                    padding: "0.4rem 0.75rem",
                    fontSize: "0.85rem",
                    backgroundColor:
                      idx === pgHighlighted
                        ? "var(--theme-primary)"
                        : isSelected
                          ? "#f0f0f0"
                          : "transparent",
                    color: idx === pgHighlighted ? "#fff" : "inherit",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  onMouseEnter={() => setPgHighlighted(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    applyProductGroup(section, index, opt);
                  }}
                >
                  {label}
                </div>
              );
            })
          ) : (
            <div
              className="dropdown-item disabled"
              style={{
                padding: "0.4rem 0.75rem",
                fontSize: "0.85rem",
                textAlign: "center",
              }}
            >
              No matching items
            </div>
          )}
        </div>
      </div>,
      portal,
    );
  };

  const openRateOnDropdown = (idx) => {
    const el = outInputRefs.current[`${idx}-item_rate_on`];
    if (el) {
      const rect = el.getBoundingClientRect();
      setRateOnPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 70),
      });
    }
    setActiveRateOnRow(idx);
    setRateOnHighlighted(
      Math.max(0, RATE_ON_OPTIONS.indexOf(outLines[idx]?.item_rate_on || "FWt")),
    );
  };

  const renderRateOnDropdown = () => {
    if (activeRateOnRow == null) return null;
    const idx = activeRateOnRow;
    const portal =
      document.getElementById("dropdown-portal") || document.body;
    return ReactDOM.createPortal(
      <div
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${rateOnPosition.top}px`,
          left: `${rateOnPosition.left}px`,
          width: `${rateOnPosition.width}px`,
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          backgroundColor: "#fff",
          boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
          padding: "0.25rem 0",
        }}
      >
        {RATE_ON_OPTIONS.map((opt, i) => (
          <div
            key={opt}
            className={`dropdown-item ${
              i === rateOnHighlighted ? "active" : ""
            }`}
            style={{
              cursor: "pointer",
              padding: "0.35rem 0.75rem",
              fontSize: "0.85rem",
              backgroundColor:
                i === rateOnHighlighted
                  ? "var(--theme-primary)"
                  : "transparent",
              color: i === rateOnHighlighted ? "#fff" : "inherit",
            }}
            onMouseEnter={() => setRateOnHighlighted(i)}
            onMouseDown={(e) => {
              e.preventDefault();
              updateOut(idx, "item_rate_on", opt);
              setActiveRateOnRow(null);
              setTimeout(() => {
                outInputRefs.current[`${idx}-item_rate`]?.focus();
              }, 40);
            }}
          >
            {opt}
          </div>
        ))}
      </div>,
      portal,
    );
  };

  const cellInput = ({ className = "", style, ...rest }) => (
    <input
      {...rest}
      className={`form-control border-0 rounded-0 ${className}`.trim()}
      style={{
        ...CELL_INPUT_STYLE,
        padding: "0.4rem 0.5rem",
        width: "100%",
        ...(style || {}),
      }}
    />
  );

  return (
    <div className="body-wrapper">
      <Toaster position="top-right" />
      <div className="container-fluid">
        <div className={`card shadow-sm ${cardClass}`}>
          <div className="px-4 py-3 border-bottom">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <h4 className="card-title mb-0">
                {settlementOnly
                  ? "Wholesale Settlement"
                  : isEdit
                    ? "Edit Wh Sales"
                    : "Wholesale Sales"}
              </h4>
              <div className="d-flex align-items-center flex-wrap gap-2 ms-auto">
                {docNumber ? (
                  <span
                    className="badge bg-primary-subtle text-primary"
                    style={{ fontSize: "0.85rem" }}
                  >
                    {docNumber}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="card-body">
            {/* Header — Date / Name / Mobile (like Ratecut) */}
            <div className="row mb-1">
              <div className="col-lg-3 col-md-4 col-sm-6 mb-2">
                <div className="d-flex align-items-center">
                  <label
                    className="form-label me-2 mb-0"
                    style={HEADER_LABEL_STYLE}
                  >
                    Date:
                  </label>
                  <input
                    type="date"
                    className={`form-control form-control-sm ${buttonClass}`}
                    value={billDate}
                    onChange={(e) => setBillDate(e.target.value)}
                    readOnly={settlementOnly}
                    style={{ fontSize: "0.85rem" }}
                  />
                </div>
              </div>
              <div className="col-lg-3 col-md-5 col-sm-6 mb-2">
                <div className="d-flex align-items-center position-relative">
                  <label
                    className="form-label me-1 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "55px", fontSize: "0.85rem" }}
                  >
                    Name:
                  </label>
                  <div className="position-relative flex-grow-1">
                    <input
                      ref={customerNameInputRef}
                      type="text"
                      className={`form-control form-control-sm ${buttonClass}`}
                      placeholder="Name"
                      value={customerSearch}
                      onChange={(e) => {
                        if (settlementOnly) return;
                        setCustomerId(null);
                        setCustomerMobile("");
                        setAmtBal(0);
                        setGoldBalGm(0);
                        setSilverBalGm(0);
                        setCustomerSearch(e.target.value);
                      }}
                      onFocus={() =>
                        !settlementOnly &&
                        customerOptions.length &&
                        setCustomerDropOpen(true)
                      }
                      onKeyDown={
                        settlementOnly ? undefined : handleCustomerNameKeyDown
                      }
                      readOnly={settlementOnly}
                      autoComplete="off"
                      style={{ fontSize: "0.85rem" }}
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-3 col-md-7 col-sm-12 mb-2">
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="text"
                    className={`form-control form-control-sm ${buttonClass}`}
                    placeholder="Mobile No"
                    value={customerMobile}
                    readOnly
                    style={{ fontSize: "0.85rem" }}
                  />
                </div>
              </div>
            </div>

            {/* Metal OUT — hidden on settlement page */}
            {!settlementOnly && (
            <div className={`${tableWrapClass} mt-3`}>
              <table className={lineTableClass}>
                <thead>
                  <tr>
                    {OUT_COLUMNS.map((col) => (
                      <th
                        key={col.id}
                        className="px-2 py-2 text-center"
                        style={{ ...TH_STYLE, width: col.width }}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {outLines.map((row, idx) => (
                    <tr key={row.key} style={{ backgroundColor: "#ffffff" }}>
                      <td className="p-0" style={{ width: OUT_COLUMNS[0].width }}>
                        <div
                          className="d-flex align-items-center justify-content-center"
                          style={{ height: 38 }}
                        >
                          <span
                            style={{
                              fontSize: "0.85rem",
                              cursor: outLines.length > 1 ? "pointer" : "default",
                            }}
                            title={outLines.length > 1 ? "Remove row" : ""}
                            onClick={() => {
                              if (outLines.length > 1) {
                                setOutLines((p) => p.filter((_, i) => i !== idx));
                              }
                            }}
                            onMouseEnter={(e) => {
                              if (outLines.length > 1) {
                                e.currentTarget.textContent = "×";
                                e.currentTarget.style.fontWeight = "600";
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.textContent = String(idx + 1);
                              e.currentTarget.style.fontWeight = "normal";
                            }}
                          >
                            {idx + 1}
                          </span>
                        </div>
                      </td>
                      <td className="p-0">
                        {cellInput({
                          ref: (el) => {
                            outInputRefs.current[`${idx}-tag_no`] = el;
                          },
                          value: row.tag_no,
                          onChange: (e) => updateOut(idx, "tag_no", e.target.value),
                          onKeyDown: (e) => onOutKeyDown(e, idx, "tag_no"),
                        })}
                      </td>
                      <td className="p-0 position-relative">
                        {cellInput({
                          ref: (el) => {
                            outInputRefs.current[`${idx}-product_group`] = el;
                          },
                          value: row.product_group_label,
                          autoComplete: "off",
                          onChange: (e) => {
                            const v = e.target.value;
                            setOutLines((prev) =>
                              prev.map((r, i) =>
                                i === idx
                                  ? {
                                      ...r,
                                      product_group_label: v,
                                      product_group_id: null,
                                    }
                                  : r,
                              ),
                            );
                            searchProductGroups(v, "out", idx);
                          },
                          onFocus: (e) => {
                            e.target.style.backgroundColor = "#fff";
                            const v = String(row.product_group_label || "").trim();
                            if (v.length >= 3) {
                              searchProductGroups(v, "out", idx);
                            } else {
                              setActivePgRow({ section: "out", index: idx });
                              positionPgDropdown("out", idx);
                            }
                          },
                          onBlur: (e) => {
                            e.target.style.backgroundColor = "transparent";
                            setTimeout(() => closePgDropdown(), 180);
                          },
                          onKeyDown: (e) => onPgKeyDown(e, "out", idx),
                        })}
                      </td>
                      {[
                        "pcs",
                        "gwt",
                        "lswt",
                        "nwt",
                        "purity",
                        "wastage",
                      ].map((field) => (
                        <td className="p-0" key={field}>
                          {cellInput({
                            ref: (el) => {
                              outInputRefs.current[`${idx}-${field}`] = el;
                            },
                            value: row[field],
                            onChange: (e) => updateOut(idx, field, e.target.value),
                            className: "text-end",
                            onKeyDown: (e) => onOutKeyDown(e, idx, field),
                          })}
                        </td>
                      ))}
                      <td className="p-0">
                        {cellInput({
                          ref: (el) => {
                            outInputRefs.current[`${idx}-fine_wt`] = el;
                          },
                          value: row.fine_wt,
                          readOnly: true,
                          className: "text-end",
                          style: { backgroundColor: "#f0f0f0", cursor: "default" },
                          onKeyDown: (e) => onOutKeyDown(e, idx, "fine_wt"),
                        })}
                      </td>
                      <td className="p-0">
                        {cellInput({
                          ref: (el) => {
                            outInputRefs.current[`${idx}-item_rate_on`] = el;
                          },
                          value: row.item_rate_on,
                          readOnly: true,
                          style: {
                            backgroundColor: "#f0f0f0",
                            cursor: "pointer",
                          },
                          onFocus: () => openRateOnDropdown(idx),
                          onClick: () => openRateOnDropdown(idx),
                          onBlur: () => {
                            setTimeout(() => {
                              if (activeRateOnRow === idx) {
                                setActiveRateOnRow(null);
                              }
                            }, 180);
                          },
                          onKeyDown: (e) => {
                            if (activeRateOnRow !== idx) {
                              if (e.key === "Enter" || e.key === "ArrowDown") {
                                e.preventDefault();
                                openRateOnDropdown(idx);
                              }
                              return;
                            }
                            if (e.key === "ArrowDown") {
                              e.preventDefault();
                              setRateOnHighlighted((h) =>
                                Math.min(h + 1, RATE_ON_OPTIONS.length - 1),
                              );
                            } else if (e.key === "ArrowUp") {
                              e.preventDefault();
                              setRateOnHighlighted((h) => Math.max(h - 1, 0));
                            } else if (e.key === "Enter") {
                              e.preventDefault();
                              updateOut(
                                idx,
                                "item_rate_on",
                                RATE_ON_OPTIONS[rateOnHighlighted] || "FWt",
                              );
                              setActiveRateOnRow(null);
                              setTimeout(() => {
                                outInputRefs.current[`${idx}-item_rate`]?.focus();
                              }, 40);
                            } else if (e.key === "Escape" || e.key === "Tab") {
                              setActiveRateOnRow(null);
                            }
                          },
                        })}
                      </td>
                      <td className="p-0">
                        {cellInput({
                          ref: (el) => {
                            outInputRefs.current[`${idx}-item_rate`] = el;
                          },
                          value: row.item_rate,
                          onChange: (e) =>
                            updateOut(idx, "item_rate", e.target.value),
                          className: "text-end",
                          onKeyDown: (e) => onOutKeyDown(e, idx, "item_rate"),
                        })}
                      </td>
                      <td className="p-0">
                        {cellInput({
                          ref: (el) => {
                            outInputRefs.current[`${idx}-amount`] = el;
                          },
                          value: row.amount,
                          onChange: (e) => updateOut(idx, "amount", e.target.value),
                          className: "text-end",
                          onKeyDown: (e) => onOutKeyDown(e, idx, "amount"),
                        })}
                      </td>
                      <td className="p-0">
                        {cellInput({
                          ref: (el) => {
                            outInputRefs.current[`${idx}-other_amt`] = el;
                          },
                          value: row.other_amt,
                          onChange: (e) => {
                            const v = e.target.value;
                            setOutLines((prev) =>
                              prev.map((r, i) =>
                                i === idx
                                  ? recalcOutDerived(
                                      {
                                        ...r,
                                        other_amt: v,
                                        otherAmountEntries: [],
                                      },
                                      { fromField: "other_amt" },
                                    )
                                  : r,
                              ),
                            );
                          },
                          className: "text-end",
                          title:
                            row.otherAmountEntries?.length > 0
                              ? "Double-click or press Enter twice to edit details"
                              : "Enter amount or double-click / Enter twice for charge details",
                          onDoubleClick: (e) =>
                            handleOtherAmountInteraction(idx, e),
                          onKeyDown: (e) => {
                            handleOtherAmountInteraction(idx, e);
                            onOutKeyDown(e, idx, "other_amt");
                          },
                        })}
                      </td>
                      <td className="p-0">
                        {cellInput({
                          ref: (el) => {
                            outInputRefs.current[`${idx}-total_amt`] = el;
                          },
                          value: row.total_amt,
                          onChange: (e) =>
                            updateOut(idx, "total_amt", e.target.value),
                          className: "text-end",
                          onFocus: () => onOutTotalAmtFocus(idx),
                          onKeyDown: (e) => onOutKeyDown(e, idx, "total_amt"),
                        })}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: "#f8f9fa" }}>
                    <td className="p-0">
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: 38 }}
                      >
                        <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                          Total
                        </span>
                      </div>
                    </td>
                    <td className="p-0" />
                    <td className="p-0" />
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: 38 }}
                      >
                        <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                          {sumOutField("pcs") || 0}
                        </span>
                      </div>
                    </td>
                    {["gwt", "lswt", "nwt"].map((field) => (
                      <td
                        className="p-0"
                        key={`tot-${field}`}
                        style={{ backgroundColor: "#fff" }}
                      >
                        <div
                          className="d-flex align-items-center justify-content-end px-2"
                          style={{ height: 38 }}
                        >
                          <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                            {sumOutField(field).toFixed(2)}
                          </span>
                        </div>
                      </td>
                    ))}
                    {["purity", "wastage"].map((field) => (
                      <td
                        className="p-0"
                        key={`tot-${field}`}
                        style={{ backgroundColor: "#fff" }}
                      >
                        <div
                          className="d-flex align-items-center justify-content-end px-2"
                          style={{ height: 38 }}
                        >
                          <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                            {avgOutField(field).toFixed(2)}
                          </span>
                        </div>
                      </td>
                    ))}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: 38 }}
                      >
                        <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                          {sumOutField("fine_wt").toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" />
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: 38 }}
                      >
                        <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                          {avgOutField("item_rate").toFixed(2)}
                        </span>
                      </div>
                    </td>
                    {["amount", "other_amt", "total_amt"].map((field) => (
                      <td
                        className="p-0"
                        key={`tot-${field}`}
                        style={{ backgroundColor: "#fff" }}
                      >
                        <div
                          className="d-flex align-items-center justify-content-end px-2"
                          style={{ height: 38 }}
                        >
                          <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                            {sumOutField(field).toFixed(2)}
                          </span>
                        </div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            )}

            {/* Old metal IN + party balance (Amt / Gold / Silvr) in one row */}
            <div className={`${settlementOnly ? "mt-2" : "mt-3"} mb-1`}>
              <span className="fw-semibold" style={{ fontSize: "0.9rem" }}>
                Old Metal
              </span>
            </div>
            <div className="d-flex align-items-start gap-2 flex-wrap">
              <div style={{ flex: "1 1 auto", minWidth: "240px", maxWidth: "84%" }}>
                <OldPurchaseTable
                  tableRows={oldLines}
                  setTableRows={setOldLines}
                  productGroupOptionsCache={oldPgOptionsCache}
                  setProductGroupOptionsCache={setOldPgOptionsCache}
                  masterGoldRate={masterGoldRate}
                  masterSilverRate={masterSilverRate}
                  showLiveRate={showLiveRate}
                  isSilverMode={metalType === "SILVER"}
                  defaultProductGroupLabel=""
                  productGroupMinWidth="150px"
                  cardClass={cardClass}
                  buttonTableStyle={buttonTableStyle}
                  tableClass={`theme-header-table ${tableClass}`}
                  buttonClass={buttonClass}
                  portalZIndex={9999}
                />
              </div>
              <div
                className="text-end"
                style={{
                  minWidth: "160px",
                  marginLeft: "6.5rem",
                  marginTop: "1.1rem",
                  flex: "0 0 auto",
                }}
              >
                {(() => {
                  const isSilverBill =
                    String(metalType || "").toUpperCase() === "SILVER";
                  // Amt / Dr Gold / Dr Silver: freeze values from page load (API).
                  // Do not recalculate when Ac Fine Cr / old metal change.
                  const panelAmt =
                    remainingAmount != null ? remainingAmount : amtBal;
                  const panelGold =
                    remainingBakiGm != null
                      ? isSilverBill
                        ? 0
                        : remainingBakiGm
                      : goldBalGm;
                  const panelSilver =
                    remainingBakiGm != null
                      ? isSilverBill
                        ? remainingBakiGm
                        : 0
                      : silverBalGm;
                  return (
                    <>
                      <PartyBalanceLine
                        value={panelAmt}
                        label="Amt"
                        decimals={2}
                      />
                      <PartyBalanceLine
                        value={panelGold}
                        label="Gold"
                        decimals={3}
                      />
                      <PartyBalanceLine
                        value={panelSilver}
                        label="Silver"
                        decimals={3}
                      />
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Settlement grid (Tot Fine / Ac / Os / Rate) + payments */}
            <div className="row mt-3 g-3 align-items-start">
              <div className="col-lg-5">
                <div
                  className={`border p-3 ${
                    buttonTableStyle === "square" ? "rounded-0" : "rounded-2"
                  } ${cardClass}`}
                >
                  <div className="row g-2">
                    <div className="col-6">
                      <div className="d-flex align-items-center mb-2">
                        <label style={SUMMARY_LABEL_STYLE}>Tot Fine Cr</label>
                        <input
                          readOnly
                          className={`form-control form-control-sm text-end ${buttonClass}`}
                          value={totFineCrGm ? totFineCrGm.toFixed(3) : ""}
                          style={READONLY_SUMMARY_INPUT_STYLE}
                          title="Ac Fine Cr + Old Metal Fine Wt (gm rows only)"
                        />
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <label style={SUMMARY_LABEL_STYLE}>Ac Amt Dr</label>
                        <input
                          readOnly
                          className={`form-control form-control-sm text-end ${buttonClass}`}
                          value={
                            grandTotal > 0 ? Number(grandTotal).toFixed(2) : ""
                          }
                          style={READONLY_SUMMARY_INPUT_STYLE}
                          title="Ac Fine Cr × 1gm Rate"
                        />
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <label style={SUMMARY_LABEL_STYLE}>Ac Amt Cr</label>
                        <input
                          type="number"
                          className={`form-control form-control-sm text-end ${buttonClass}`}
                          value={acAmtCr}
                          onChange={(e) => setAcAmtCr(e.target.value)}
                          style={SUMMARY_INPUT_STYLE}
                        />
                      </div>
                      <div className="d-flex align-items-center">
                        <label style={SUMMARY_LABEL_STYLE}>Os Fine</label>
                        <input
                          readOnly
                          className={`form-control form-control-sm text-end fw-semibold ${buttonClass}`}
                          value={osFineGm ? osFineGm.toFixed(3) : ""}
                          style={READONLY_SUMMARY_INPUT_STYLE}
                          title={`Baki after Ac Fine: ${statusLabel}`}
                        />
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="d-flex align-items-center mb-2">
                        <label style={SUMMARY_LABEL_STYLE}>Tot Fine Dr</label>
                        <input
                          readOnly
                          className={`form-control form-control-sm text-end ${buttonClass}`}
                          value={
                            isEdit || settlementOnly
                              ? "0.000"
                              : totalOutGm
                                ? totalOutGm.toFixed(3)
                                : ""
                          }
                          style={READONLY_SUMMARY_INPUT_STYLE}
                        />
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <label style={SUMMARY_LABEL_STYLE}>Ac Fine Cr</label>
                        <input
                          type="number"
                          className={`form-control form-control-sm text-end ${buttonClass}`}
                          value={paidGm}
                          onChange={(e) => setPaidGm(e.target.value)}
                          onDoubleClick={fillPaidToClearBaki}
                          title="Double-click to clear Os Fine"
                          style={SUMMARY_INPUT_STYLE}
                        />
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <label style={SUMMARY_LABEL_STYLE}>Ac Fine Dr</label>
                        <input
                          type="number"
                          className={`form-control form-control-sm text-end ${buttonClass}`}
                          value={acFineDr}
                          onChange={(e) => setAcFineDr(e.target.value)}
                          style={SUMMARY_INPUT_STYLE}
                        />
                      </div>
                      <div className="d-flex align-items-center">
                        <label style={SUMMARY_LABEL_STYLE}>1gm Rate</label>
                        <input
                          type="number"
                          className={`form-control form-control-sm text-end ${buttonClass}`}
                          value={itemRate}
                          onChange={(e) => setItemRate(e.target.value)}
                          style={SUMMARY_INPUT_STYLE}
                        />
                      </div>
                    </div>
                  </div>

                  {(() => {
                    const settleNet =
                      settlementOnly && paidGmNum <= 0
                        ? Math.max(0, Number(remainingAmount) || 0)
                        : grandTotal;
                    const overPay = totalPaidMoney - settleNet;
                    if (settlementOnly && overPay > 0.05) {
                      return (
                        <div className="text-success small mt-2">
                          Jama {formatIndianRupee(overPay)} (extra to customer
                          account)
                        </div>
                      );
                    }
                    if (settleNet > 0 && overPay > 0.05) {
                      return (
                        <div className="text-danger small mt-2">
                          Payments {formatIndianRupee(totalPaidMoney)} exceed Ac
                          Amt Dr
                        </div>
                      );
                    }
                    if (settleNet > 0 && settleNet - totalPaidMoney > 0.05) {
                      return (
                        <div className="text-muted small mt-2">
                          OS / udhar{" "}
                          {formatIndianRupee(settleNet - totalPaidMoney)}
                        </div>
                      );
                    }
                    return null;
                  })()}
                  <button
                    type="button"
                    className="btn btn-link btn-sm px-0 mt-1"
                    style={{ fontSize: "0.8rem" }}
                    onClick={syncCashPaymentToGrand}
                  >
                    Match cash to Ac Amt Dr
                  </button>
                </div>
              </div>
              <div className="col-lg-7">
                <PaymentSection
                  paymentRows={paymentRows}
                  onPaymentRowsChange={setPaymentRows}
                  paymentTypes={paymentTypes}
                  bankAccounts={bankAccounts}
                  excludedPayModes={[
                    "old_gold",
                    "old_silver",
                    "scheme",
                    "order",
                  ]}
                  payableRows={payableRows}
                  onPayableRowsChange={setPayableRows}
                  note={notes}
                  onNoteChange={setNotes}
                  finalAmount={(
                    settlementOnly && paidGmNum <= 0
                      ? Math.max(0, Number(remainingAmount) || 0)
                      : grandTotal
                  ).toFixed(2)}
                  showDiscount={false}
                  showGst={false}
                  showSummaryPanel={false}
                  showNetAmtRow
                  showOsAmtRow
                  netAmtValue={
                    settlementOnly && paidGmNum <= 0
                      ? Math.max(0, Number(remainingAmount) || 0)
                      : grandTotal
                  }
                  osAmtValue={Math.max(
                    0,
                    (settlementOnly && paidGmNum <= 0
                      ? Math.max(0, Number(remainingAmount) || 0)
                      : grandTotal) - totalPaidMoney,
                  )}
                  discountType="amount"
                  onDiscountTypeChange={() => {}}
                  discountValue="0"
                  onDiscountValueChange={() => {}}
                  discountAmount="0.00"
                  gstPercentage="0"
                  onGstPercentageChange={() => {}}
                  gstAmount="0.00"
                  roundOffAmount="0"
                  onRoundOffChange={() => {}}
                  grandTotal={
                    settlementOnly && paidGmNum <= 0
                      ? Math.max(0, Number(remainingAmount) || 0)
                      : grandTotal
                  }
                  totalPaid={totalPaidMoney}
                  payableAmount={0}
                  remainingAmount={0}
                  followUpDate=""
                  onFollowUpDateChange={() => {}}
                  buttonClass={buttonClass}
                  cardClass={cardClass}
                  buttonTableStyle={buttonTableStyle}
                  grandTotalLabel="Payable"
                />
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-12">
                <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
                  {(settlementOnly || !isEdit) && (
                    <button
                      type="button"
                      className={`btn btn-secondary ${buttonClass}`}
                      onClick={() => navigate("/whinvoice")}
                    >
                      Cancel
                    </button>
                  )}
                  {canSave && (
                    <button
                      type="button"
                      className={`btn btn-primary ${buttonClass}`}
                      disabled={submitting}
                      onClick={handleSubmit}
                    >
                      {submitting
                        ? "Saving..."
                        : settlementOnly
                          ? "Save"
                          : isEdit
                            ? "Update"
                            : "Save"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {renderCustomerNameDropdown()}
      {renderPgDropdown()}
      {renderRateOnDropdown()}
      <OtherAmountModal
        otherAmountModal={otherAmountModal}
        setOtherAmountModal={setOtherAmountModal}
        cardClass={cardClass}
        otherAmountColumnWidths={otherAmountColumnWidths}
        handleOtherAmountColumnWidthChange={handleOtherAmountColumnWidthChange}
        suggestions={suggestions}
        setSuggestions={setSuggestions}
        getSuggestions={getSuggestions}
        applyChargeType={(entry, chargeName) =>
          applyOtherChargeToEntry(entry, chargeTypes, chargeName)
        }
        chargeOptions={chargeTypes}
        formatAmount={formatAmount}
        calculateAmount={calculateAmount}
        rateOnOptions={otherRateOnOptions}
        highlightedIndex={highlightedIndex}
        setHighlightedIndex={setHighlightedIndex}
        handleRateOnDropdown={handleRateOnDropdown}
        handleRateOnKeyDown={handleRateOnKeyDown}
        calculateLessWeight={calculateLessWeight}
        formatLessWeight={formatLessWeight}
        calculateTotal={sumOtherAmountEntries}
        buttonClass={buttonClass}
        handleSaveOtherAmountEntries={handleSaveOtherAmountEntries}
        setTableData={setTableDataFromOtherModal}
      />
    </div>
  );
}

export default Wholesale;
